import {
  EEntityState,
  FolderResponse,
  MediaSequenceCreateNewRequest,
  UserSimpleResponse,
} from 'api/core';
import { useCreateMediaSequenceNew } from 'api/useMediaSequencesApi';
import { AddressSuggestion } from 'components/Form/AutocompleteAddress';
import { InlineLoading } from 'components/Loading';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { MediaSequenceCreateFormConfigurationStep } from './MediaSequenceCreateFormConfigurationStep';
import { MediaSequenceCreateFormTemplateStep } from './MediaSequenceCreateFormTemplateStep';

enum EStep {
  Configuration,
  // This step can either be "Bones menu" templates or a predefined one
  Templates,
}

const translateStep = (step: EStep) => {
  switch (step) {
    case EStep.Configuration:
      return 'Opsætning';
    case EStep.Templates:
      return 'Skabeloner';
    default:
      return 'Ukendt';
  }
};

const STEPS = [EStep.Configuration, EStep.Templates];

interface MediaSequenceCreateFormNewProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  inDialog?: boolean;
}

export const MediaSequenceCreateFormNew = ({
  onSuccess,
  onCancel,
  inDialog,
}: MediaSequenceCreateFormNewProps) => {
  const [currentStep, setCurrentStep] = useState(STEPS[0]);

  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AddressSuggestion>();

  const [selectedMetaMappingUser, setSelectedMetaMappingUser] =
    useState<UserSimpleResponse>();

  const [selectedFolder, setSelectedFolder] = useState<FolderResponse>();

  const navigate = useNavigate();
  const { mutateAsync: createAsync, isPending } = useCreateMediaSequenceNew();

  const methods = useForm<MediaSequenceCreateNewRequest>({
    defaultValues: {
      caseId: null,
      folderId: null,
      metaMappingUserId: null,
    },
  });

  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods;

  const onSubmit = handleSubmit(async (result) => {
    if (selectedSuggestion && selectedSuggestion.forslagstekst) {
      result.name = selectedSuggestion.forslagstekst;
      result.dawaAddressId = selectedSuggestion.data.id;
    }
    result.entityState = EEntityState.Active;
    const mediaSequence = await createAsync({
      mediaSequenceCreateNewRequest: result,
    });
    onSuccess?.();
    // Redirect user to the first step which is templates
    navigate(`/media-sequences/${mediaSequence.id}/setup/1`);
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="space-y-2">
        <ul className="steps steps-horizontal w-full">
          {STEPS.map((s, index) => (
            <li
              id={`step-${index + 1}`}
              key={index}
              onClick={() => {
                if (isPending) return;
                setCurrentStep(Math.min(currentStep, s));
              }}
              className={twMerge(
                'step !min-w-32 pb-4 md:pb-0',
                isPending && 'step-disabled text-gray-400 cursor-not-allowed',
                s == currentStep && 'font-bold',
                !isPending &&
                  s <= currentStep &&
                  'cursor-pointer step-primary after:!text-white hover:font-bold hover:step-info'
              )}
            >
              <span>{translateStep(s)}</span>
            </li>
          ))}
        </ul>
        <div className="divider my-0"></div>
        {currentStep === EStep.Configuration ? (
          <MediaSequenceCreateFormConfigurationStep
            inDialog={inDialog}
            setSelectedSuggestion={setSelectedSuggestion}
            selectedMetaMappingUser={selectedMetaMappingUser}
            setSelectedMetaMappingUser={setSelectedMetaMappingUser}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
          />
        ) : null}
        {currentStep === EStep.Templates ? (
          <MediaSequenceCreateFormTemplateStep
            inDialog={inDialog}
            disabled={isPending}
          />
        ) : null}

        {/* TODO: Improve */}
        {isPending ? (
          <InlineLoading text="Opretter video..." icon="loading-icon" />
        ) : null}

        <div className="flex justify-center space-x-4 pt-4">
          {currentStep !== STEPS[STEPS.length - 1] ? (
            <button
              data-testid="smart-text-next-step"
              className="btn btn-primary"
              disabled={
                isPending ||
                (currentStep === EStep.Configuration && !watch('name'))
              }
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setCurrentStep((e) => e + 1);
              }}
            >
              Fortsæt
            </button>
          ) : (
            <button
              data-testid="smart-text-submit"
              type="submit"
              className="btn btn-primary"
              disabled={isPending || !isValid}
            >
              Opret
            </button>
          )}

          {onCancel ? (
            <button type="button" className="btn" onClick={onCancel}>
              Annuller
            </button>
          ) : null}
        </div>
      </form>
    </FormProvider>
  );
};
